import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { customerService } from '../services/customerService';
import { invoiceService } from '../services/invoiceService';
import { useClient } from '../context/ClientContext';

const {
  FiUsers,
  FiUserPlus,
  FiSearch,
  FiPlus,
  FiX,
  FiArrowLeft,
  FiEdit2,
  FiMail,
  FiPhone,
  FiGlobe,
  FiMapPin,
  FiFileText,
  FiDownload,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiDollarSign,
  FiChevronRight,
  FiLoader,
  FiArchive,
  FiRotateCcw,
  FiExternalLink,
  FiCreditCard,
  FiInfo
} = FiIcons;

const inputClass =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-[13px] text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10';

const emptyForm = {
  name: '',
  company_name: '',
  contact_name: '',
  email: '',
  phone: '',
  customer_number: '',
  currency: 'USD',
  payment_terms_days: 30,
  tax_id: '',
  website: '',
  notes: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'US'
};

const currencyOptions = [
  ['USD', 'USD — US Dollar'],
  ['EUR', 'EUR — Euro'],
  ['GBP', 'GBP — British Pound'],
  ['CAD', 'CAD — Canadian Dollar'],
  ['AUD', 'AUD — Australian Dollar'],
  ['PHP', 'PHP — Philippine Peso'],
  ['SGD', 'SGD — Singapore Dollar'],
  ['JPY', 'JPY — Japanese Yen']
];

const paymentTerms = [
  [0, 'Due on receipt'],
  [7, 'Net 7'],
  [15, 'Net 15'],
  [30, 'Net 30'],
  [45, 'Net 45'],
  [60, 'Net 60'],
  [90, 'Net 90']
];

function formatMoney(amount, currency = 'USD') {
  const value = (Number(amount) || 0) / 100;

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

function formatDate(date) {
  if (!date) return '—';

  const parsed = new Date(`${String(date).slice(0, 10)}T12:00:00`);

  if (Number.isNaN(parsed.getTime())) return '—';

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function getInitials(name) {
  return String(name || 'CU')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase();
}

function getInvoiceStatus(invoice) {
  const status = String(invoice.status || '').toLowerCase();
  const due = Number(invoice.amount_due) || 0;
  const today = new Date().toLocaleDateString('en-CA');

  if (
    ['open', 'sent', 'unpaid', 'partially_paid', 'overdue'].includes(status) &&
    due > 0 &&
    invoice.due_date &&
    invoice.due_date < today
  ) {
    return 'overdue';
  }

  return status;
}

function invoiceStatusClass(status) {
  switch (status) {
    case 'paid':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    case 'overdue':
      return 'bg-rose-50 text-rose-700 border-rose-100';
    case 'draft':
      return 'bg-slate-50 text-slate-600 border-slate-200';
    case 'void':
    case 'voided':
    case 'uncollectible':
      return 'bg-slate-100 text-slate-500 border-slate-200';
    default:
      return 'bg-amber-50 text-amber-700 border-amber-100';
  }
}

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
        active
          ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
          : 'border-slate-200 bg-slate-50 text-slate-500'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          active ? 'bg-emerald-500' : 'bg-slate-400'
        }`}
      />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function SummaryCard({ title, value, subtitle, icon, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600'
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12px] font-medium text-slate-500">
            {title}
          </p>
          <p className="mt-2 break-words text-[22px] font-semibold tracking-tight text-slate-900">
            {value}
          </p>
          {subtitle && (
            <p className="mt-1 text-[11px] text-slate-400">
              {subtitle}
            </p>
          )}
        </div>

        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            colors[color] || colors.blue
          }`}
        >
          <SafeIcon icon={icon} className="text-[18px]" />
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="mb-1.5 block text-[12px] font-medium text-slate-700">
        {label}
        {required && <span className="ml-1 text-rose-500">*</span>}
      </label>
      {children}
      {hint && (
        <p className="mt-1 text-[11px] text-slate-400">
          {hint}
        </p>
      )}
    </div>
  );
}

function SectionHeader({ number, title, description }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[12px] font-semibold text-blue-600">
        {number}
      </span>
      <div>
        <h3 className="text-[14px] font-semibold text-slate-900">
          {title}
        </h3>
        {description && (
          <p className="mt-0.5 text-[12px] text-slate-500">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

function CustomerForm({
  form,
  setForm,
  editing,
  saving,
  error,
  onCancel,
  onSubmit
}) {
  const change = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value
    }));
  };

  return (
    <div className="mx-auto max-w-5xl animate-in fade-in duration-300">
      <div className="mb-5 flex items-center justify-between">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-500 transition hover:text-blue-600 disabled:opacity-50"
        >
          <SafeIcon icon={FiArrowLeft} />
          Back to Customers
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50/60 px-5 py-5 sm:px-7">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <SafeIcon icon={editing ? FiEdit2 : FiUserPlus} className="text-xl" />
          </div>
          <div>
            <h1 className="text-[19px] font-semibold text-slate-900">
              {editing ? 'Edit Customer' : 'New Customer'}
            </h1>
            <p className="mt-0.5 text-[12px] text-slate-500">
              Maintain accurate customer and billing information.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit}>
          <div className="space-y-8 p-5 sm:p-7">
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-3 text-[12px] text-rose-700">
                <SafeIcon icon={FiAlertCircle} className="mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            <section>
              <SectionHeader
                number="1"
                title="Customer information"
                description="Identify the person or business you invoice."
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="Customer display name" required>
                    <input
                      required
                      maxLength={200}
                      className={inputClass}
                      placeholder="Acme Corporation"
                      value={form.name}
                      onChange={(e) => change('name', e.target.value)}
                    />
                  </Field>
                </div>

                <Field label="Company / legal business name">
                  <input
                    className={inputClass}
                    placeholder="Optional business name"
                    value={form.company_name}
                    onChange={(e) => change('company_name', e.target.value)}
                  />
                </Field>

                <Field label="Primary contact">
                  <input
                    className={inputClass}
                    placeholder="Full contact name"
                    value={form.contact_name}
                    onChange={(e) => change('contact_name', e.target.value)}
                  />
                </Field>

                <Field label="Email address">
                  <input
                    type="email"
                    className={inputClass}
                    placeholder="billing@company.com"
                    value={form.email}
                    onChange={(e) => change('email', e.target.value)}
                  />
                </Field>

                <Field label="Phone number">
                  <input
                    type="tel"
                    className={inputClass}
                    placeholder="+1 (555) 000-0000"
                    value={form.phone}
                    onChange={(e) => change('phone', e.target.value)}
                  />
                </Field>

                <Field
                  label="Customer number"
                  hint="Optional unique reference within this organization."
                >
                  <input
                    className={inputClass}
                    placeholder="CUST-0001"
                    value={form.customer_number}
                    onChange={(e) => change('customer_number', e.target.value)}
                  />
                </Field>

                <Field label="Website">
                  <input
                    type="text"
                    className={inputClass}
                    placeholder="https://company.com"
                    value={form.website}
                    onChange={(e) => change('website', e.target.value)}
                  />
                </Field>
              </div>
            </section>

            <div className="border-t border-slate-100" />

            <section>
              <SectionHeader
                number="2"
                title="Billing preferences"
                description="Default settings for future invoices."
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Default currency">
                  <select
                    className={inputClass}
                    value={form.currency}
                    onChange={(e) => change('currency', e.target.value)}
                  >
                    {currencyOptions.map(([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Payment terms">
                  <select
                    className={inputClass}
                    value={form.payment_terms_days}
                    onChange={(e) =>
                      change('payment_terms_days', Number(e.target.value))
                    }
                  >
                    {paymentTerms.map(([days, label]) => (
                      <option key={days} value={days}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>

                <div className="sm:col-span-2">
                  <Field
                    label="Tax ID / business registration"
                    hint="Internal reference. Use only where needed and restrict access appropriately."
                  >
                    <input
                      className={inputClass}
                      placeholder="Optional tax identification number"
                      value={form.tax_id}
                      onChange={(e) => change('tax_id', e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            </section>

            <div className="border-t border-slate-100" />

            <section>
              <SectionHeader
                number="3"
                title="Billing address"
                description="Address to use for customer billing records."
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="Street address">
                    <input
                      className={inputClass}
                      placeholder="Street address"
                      value={form.address_line1}
                      onChange={(e) => change('address_line1', e.target.value)}
                    />
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="Apartment, suite, or unit">
                    <input
                      className={inputClass}
                      placeholder="Optional"
                      value={form.address_line2}
                      onChange={(e) => change('address_line2', e.target.value)}
                    />
                  </Field>
                </div>

                <Field label="City">
                  <input
                    className={inputClass}
                    value={form.city}
                    onChange={(e) => change('city', e.target.value)}
                  />
                </Field>

                <Field label="State / province">
                  <input
                    className={inputClass}
                    value={form.state}
                    onChange={(e) => change('state', e.target.value)}
                  />
                </Field>

                <Field label="Postal / ZIP code">
                  <input
                    className={inputClass}
                    value={form.postal_code}
                    onChange={(e) => change('postal_code', e.target.value)}
                  />
                </Field>

                <Field
                  label="Country code"
                  hint="Two-letter ISO country code, e.g. US or PH."
                >
                  <input
                    className={inputClass}
                    maxLength={2}
                    placeholder="US"
                    value={form.country}
                    onChange={(e) => change('country', e.target.value.toUpperCase())}
                  />
                </Field>
              </div>
            </section>

            <div className="border-t border-slate-100" />

            <section>
              <SectionHeader
                number="4"
                title="Internal notes"
                description="Private notes for your accounting team."
              />

              <textarea
                rows={4}
                className={`${inputClass} resize-y`}
                placeholder="Billing instructions, customer preferences, or other relevant details..."
                value={form.notes}
                onChange={(e) => change('notes', e.target.value)}
              />
            </section>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/70 px-5 py-4 sm:px-7">
            <button
              type="button"
              disabled={saving}
              onClick={onCancel}
              className="rounded-lg px-4 py-2.5 text-[13px] font-medium text-slate-600 hover:bg-slate-200/60 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-[13px] font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <SafeIcon
                icon={saving ? FiLoader : FiCheckCircle}
                className={saving ? 'animate-spin' : ''}
              />
              {saving
                ? 'Saving...'
                : editing
                  ? 'Save Changes'
                  : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MoneyBreakdown({ amounts, field, fallbackCurrency }) {
  const values = (amounts || []).filter(
    (item) => Number(item[field]) > 0
  );

  if (!values.length) {
    return (
      <span>{formatMoney(0, fallbackCurrency)}</span>
    );
  }

  return (
    <div className="space-y-0.5">
      {values.map((item) => (
        <div key={item.currency}>
          {formatMoney(item[field], item.currency)}
        </div>
      ))}
    </div>
  );
}

function CustomerDetails({
  customer,
  invoices,
  onBack,
  onEdit,
  onToggleActive,
  busy,
  defaultCurrency
}) {
  const customerInvoices = useMemo(
    () => invoices.filter((invoice) => invoice.customer_id === customer.id),
    [invoices, customer.id]
  );

  const summary = useMemo(
    () => customerService.calculateCustomerSummary(customerInvoices),
    [customerInvoices]
  );

  const address = [
    customer.address_line1,
    customer.address_line2,
    customer.city,
    customer.state,
    customer.postal_code,
    customer.country
  ].filter(Boolean).join(', ');

  const website = String(customer.website || '').trim();
  const websiteUrl = /^https?:\/\//i.test(website)
    ? website
    : `https://${website}`;

  return (
    <div className="mx-auto max-w-[1500px] animate-in fade-in duration-300">
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-2 text-[13px] font-medium text-slate-500 hover:text-blue-600"
      >
        <SafeIcon icon={FiArrowLeft} />
        Back to Customers
      </button>

      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[18px] font-semibold text-blue-600">
              {getInitials(customer.name)}
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="break-words text-[21px] font-semibold text-slate-900">
                  {customer.name}
                </h1>
                <StatusBadge active={customer.is_active} />
              </div>

              {customer.company_name && (
                <p className="mt-1 text-[13px] text-slate-500">
                  {customer.company_name}
                </p>
              )}

              {customer.customer_number && (
                <p className="mt-1 text-[12px] text-slate-400">
                  Customer #{customer.customer_number}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onEdit(customer)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] font-medium text-slate-700 hover:bg-slate-50"
            >
              <SafeIcon icon={FiEdit2} />
              Edit
            </button>

            <button
              type="button"
              disabled={busy}
              onClick={() => onToggleActive(customer)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              <SafeIcon
                icon={customer.is_active ? FiArchive : FiRotateCcw}
              />
              {customer.is_active ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          title="Total Invoiced"
          value={
            <MoneyBreakdown
              amounts={summary}
              field="totalInvoiced"
              fallbackCurrency={customer.currency || defaultCurrency}
            />
          }
          subtitle="Excludes drafts and voided invoices"
          icon={FiFileText}
        />
        <SummaryCard
          title="Outstanding Balance"
          value={
            <MoneyBreakdown
              amounts={summary}
              field="outstanding"
              fallbackCurrency={customer.currency || defaultCurrency}
            />
          }
          subtitle="Remaining receivables"
          icon={FiDollarSign}
          color="amber"
        />
        <SummaryCard
          title="Overdue Balance"
          value={
            <MoneyBreakdown
              amounts={summary}
              field="overdue"
              fallbackCurrency={customer.currency || defaultCurrency}
            />
          }
          subtitle="Past-due unpaid invoices"
          icon={FiClock}
          color="rose"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-5 text-[14px] font-semibold text-slate-900">
              Contact Information
            </h2>

            <div className="space-y-4 text-[12px]">
              {[
                [FiUsers, 'Primary contact', customer.contact_name],
                [FiMail, 'Email', customer.email],
                [FiPhone, 'Phone', customer.phone],
                [FiGlobe, 'Website', website],
                [FiMapPin, 'Billing address', address]
              ].map(([Icon, label, value]) => (
                <div key={label} className="flex items-start gap-3">
                  <SafeIcon
                    icon={Icon}
                    className="mt-0.5 shrink-0 text-[15px] text-slate-400"
                  />
                  <div className="min-w-0">
                    <p className="text-slate-400">{label}</p>
                    {label === 'Website' && website ? (
                      <a
                        href={websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 block break-all font-medium text-blue-600 hover:underline"
                      >
                        {website}
                      </a>
                    ) : (
                      <p className="mt-1 break-words font-medium text-slate-700">
                        {value || 'Not provided'}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="mb-4 text-[14px] font-semibold text-slate-900">
              Billing Settings
            </h2>

            <div className="space-y-3 text-[12px]">
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">Currency</span>
                <span className="font-medium text-slate-800">
                  {customer.currency || 'USD'}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">Payment terms</span>
                <span className="font-medium text-slate-800">
                  {Number(customer.payment_terms_days) === 0
                    ? 'Due on receipt'
                    : `Net ${customer.payment_terms_days ?? 30}`}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-slate-500">Stripe customer</span>
                <span className="font-medium text-slate-800">
                  {customer.stripe_customer_id ? 'Linked' : 'Not linked'}
                </span>
              </div>
            </div>
          </section>

          {customer.notes && (
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-3 text-[14px] font-semibold text-slate-900">
                Internal Notes
              </h2>
              <p className="whitespace-pre-wrap break-words text-[12px] leading-relaxed text-slate-600">
                {customer.notes}
              </p>
            </section>
          )}
        </div>

        <div className="lg:col-span-2">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-[14px] font-semibold text-slate-900">
                  Invoice History
                </h2>
                <p className="mt-1 text-[12px] text-slate-500">
                  {customerInvoices.length} invoice(s) associated with this customer
                </p>
              </div>
              <SafeIcon icon={FiFileText} className="text-lg text-slate-400" />
            </div>

            {customerInvoices.length === 0 ? (
              <div className="flex flex-col items-center px-5 py-14 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-50">
                  <SafeIcon icon={FiFileText} className="text-xl text-slate-300" />
                </div>
                <p className="text-[13px] font-medium text-slate-700">
                  No invoices yet
                </p>
                <p className="mt-1 text-[12px] text-slate-400">
                  Invoices for this customer will appear here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-[10px] uppercase tracking-wide text-slate-400">
                      <th className="px-5 py-3 font-medium">Invoice</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Amount</th>
                      <th className="px-4 py-3 font-medium">Balance</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 text-right font-medium">Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerInvoices.map((invoice) => {
                      const status = getInvoiceStatus(invoice);

                      return (
                        <tr
                          key={invoice.id}
                          className="border-t border-slate-100 text-[12px] hover:bg-slate-50/60"
                        >
                          <td className="px-5 py-3 font-medium text-slate-800">
                            {invoice.invoice_number || 'Draft'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {formatDate(invoice.issue_date)}
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-700">
                            {formatMoney(
                              invoice.total_amount,
                              invoice.currency || defaultCurrency
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {formatMoney(
                              invoice.amount_due,
                              invoice.currency || defaultCurrency
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded-md border px-2 py-1 text-[10px] font-medium capitalize ${invoiceStatusClass(status)}`}
                            >
                              {status === 'open' || status === 'sent'
                                ? 'Unpaid'
                                : status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right">
                            {invoice.hosted_invoice_url ? (
                              <a
                                href={invoice.hosted_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex rounded-md p-2 text-blue-600 hover:bg-blue-50"
                                title="Open Stripe invoice"
                              >
                                <SafeIcon icon={FiExternalLink} />
                              </a>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default function Customers() {
  const { activeClient } = useClient();

  const organizationId = activeClient?.id;
  const defaultCurrency =
    activeClient?.currency ||
    activeClient?.billingCurrency ||
    'USD';

  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState('');

  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('active');

  const fetchData = useCallback(async (silent = false) => {
    if (!organizationId) {
      setCustomers([]);
      setInvoices([]);
      setLoading(false);
      return;
    }

    if (silent) setRefreshing(true);
    else setLoading(true);

    setError('');

    try {
      const [customerData, invoiceData] = await Promise.all([
        customerService.getCustomers(organizationId),
        invoiceService.getInvoices(organizationId)
      ]);

      setCustomers(customerData || []);
      setInvoices(invoiceData || []);
    } catch (err) {
      console.error('Customers page load failed:', err);
      setError(err.message || 'Unable to load customer information.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [organizationId]);

  useEffect(() => {
    setView('list');
    setSelectedId(null);
    setEditingId(null);
    setSuccess('');
    setCustomers([]);
    setInvoices([]);
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const invoicesByCustomer = useMemo(() => {
    const grouped = new Map();

    for (const invoice of invoices) {
      if (!invoice.customer_id) continue;

      const current = grouped.get(invoice.customer_id) || [];
      current.push(invoice);
      grouped.set(invoice.customer_id, current);
    }

    return grouped;
  }, [invoices]);

  const customerSummaries = useMemo(() => {
    const result = new Map();

    for (const customer of customers) {
      result.set(
        customer.id,
        customerService.calculateCustomerSummary(
          invoicesByCustomer.get(customer.id) || []
        )
      );
    }

    return result;
  }, [customers, invoicesByCustomer]);

  const overallSummary = useMemo(() => {
    return customerService.calculateCustomerSummary(invoices);
  }, [invoices]);

  const activeCount = customers.filter((c) => c.is_active).length;

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();

    return customers.filter((customer) => {
      if (filter === 'active' && !customer.is_active) return false;
      if (filter === 'inactive' && customer.is_active) return false;

      if (!term) return true;

      return [
        customer.name,
        customer.company_name,
        customer.contact_name,
        customer.email,
        customer.phone,
        customer.customer_number
      ].some((value) =>
        String(value || '').toLowerCase().includes(term)
      );
    });
  }, [customers, search, filter]);

  const selectedCustomer = customers.find(
    (customer) => customer.id === selectedId
  );

  const openNew = () => {
    setForm({
      ...emptyForm,
      currency: defaultCurrency
    });
    setEditingId(null);
    setSaveError('');
    setSuccess('');
    setView('form');
  };

  const openEdit = (customer) => {
    const nextForm = {};

    for (const field of Object.keys(emptyForm)) {
      nextForm[field] = customer[field] ?? emptyForm[field];
    }

    setForm(nextForm);
    setEditingId(customer.id);
    setSaveError('');
    setSuccess('');
    setView('form');
  };

  const openDetails = (customer) => {
    setSelectedId(customer.id);
    setView('details');
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (saving) return;

    setSaveError('');

    if (!organizationId) {
      setSaveError('Please select a client organization.');
      return;
    }

    if (!form.name.trim()) {
      setSaveError('Customer name is required.');
      return;
    }

    setSaving(true);

    try {
      if (editingId) {
        await customerService.updateCustomer(
          organizationId,
          editingId,
          form
        );
      } else {
        await customerService.createCustomer(
          form,
          organizationId
        );
      }

      await fetchData(true);

      setView(editingId ? 'details' : 'list');
      setSuccess(
        editingId
          ? 'Customer updated successfully.'
          : 'Customer created successfully.'
      );
      setEditingId(null);
    } catch (err) {
      console.error('Customer save failed:', err);
      setSaveError(err.message || 'Unable to save customer.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (customer) => {
    if (actionBusy || !organizationId) return;

    const action = customer.is_active ? 'deactivate' : 'reactivate';

    const confirmed = window.confirm(
      customer.is_active
        ? `Deactivate ${customer.name}?\n\nThe customer and their accounting history will be preserved.`
        : `Reactivate ${customer.name}?`
    );

    if (!confirmed) return;

    setActionBusy(true);
    setError('');

    try {
      if (customer.is_active) {
        await customerService.deactivateCustomer(
          organizationId,
          customer.id
        );
      } else {
        await customerService.reactivateCustomer(
          organizationId,
          customer.id
        );
      }

      await fetchData(true);

      setSuccess(
        `Customer ${action === 'deactivate' ? 'deactivated' : 'reactivated'} successfully.`
      );
    } catch (err) {
      console.error('Customer status update failed:', err);
      setError(err.message || `Unable to ${action} customer.`);
    } finally {
      setActionBusy(false);
    }
  };

  const exportCSV = () => {
    const headers = [
      'Customer Number',
      'Customer Name',
      'Company Name',
      'Primary Contact',
      'Email',
      'Phone',
      'Currency',
      'Payment Terms (Days)',
      'Address Line 1',
      'Address Line 2',
      'City',
      'State',
      'Postal Code',
      'Country',
      'Status'
    ];

    const fields = [
      'customer_number',
      'name',
      'company_name',
      'contact_name',
      'email',
      'phone',
      'currency',
      'payment_terms_days',
      'address_line1',
      'address_line2',
      'city',
      'state',
      'postal_code',
      'country',
      'is_active'
    ];

    const escapeCSV = (value) => {
      let text = String(value ?? '');

      // Prevent spreadsheet formula execution when opening exported CSVs.
      if (/^[\s]*[=+\-@]/.test(text)) {
        text = `'${text}`;
      }

      return `"${text.replace(/"/g, '""')}"`;
    };

    const rows = filteredCustomers.map((customer) =>
      fields.map((field) => {
        if (field === 'is_active') {
          return escapeCSV(customer.is_active ? 'Active' : 'Inactive');
        }

        return escapeCSV(customer[field]);
      }).join(',')
    );

    const csv = [
      headers.map(escapeCSV).join(','),
      ...rows
    ].join('\r\n');

    const blob = new Blob(['\uFEFF', csv], {
      type: 'text/csv;charset=utf-8;'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `profityo-customers-${new Date().toISOString().slice(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center gap-3 text-slate-500">
        <SafeIcon
          icon={FiLoader}
          className="animate-spin text-[25px] text-blue-600"
        />
        <p className="text-[13px]">Loading customers...</p>
      </div>
    );
  }

  if (view === 'form') {
    return (
      <CustomerForm
        form={form}
        setForm={setForm}
        editing={Boolean(editingId)}
        saving={saving}
        error={saveError}
        onCancel={() => {
          setView(editingId && selectedId ? 'details' : 'list');
          setEditingId(null);
        }}
        onSubmit={handleSave}
      />
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] animate-in fade-in duration-300">
      {error && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-rose-100 bg-rose-50 p-3 text-[12px] text-rose-700">
          <SafeIcon icon={FiAlertCircle} className="shrink-0 text-base" />
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => fetchData(true)}
            className="font-semibold underline"
          >
            Retry
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-[12px] text-emerald-700">
          <SafeIcon icon={FiCheckCircle} className="shrink-0 text-base" />
          <span className="flex-1">{success}</span>
          <button
            type="button"
            onClick={() => setSuccess('')}
            className="rounded p-1 hover:bg-emerald-100"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
      )}

      {view === 'details' && selectedCustomer ? (
        <CustomerDetails
          customer={selectedCustomer}
          invoices={invoices}
          defaultCurrency={defaultCurrency}
          onBack={() => setView('list')}
          onEdit={openEdit}
          onToggleActive={handleToggleActive}
          busy={actionBusy}
        />
      ) : (
        <>
          <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <div className="flex items-center gap-2.5">
                <SafeIcon
                  icon={FiUsers}
                  className="text-[23px] text-blue-600"
                />
                <h1 className="text-[23px] font-semibold tracking-tight text-slate-900">
                  Customers
                </h1>
              </div>
              <p className="mt-1 text-[13px] text-slate-500">
                Manage customer relationships, billing details, and receivables.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={refreshing}
                onClick={() => fetchData(true)}
                title="Refresh customers"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 disabled:opacity-50"
              >
                <SafeIcon
                  icon={FiRefreshCw}
                  className={refreshing ? 'animate-spin' : ''}
                />
              </button>

              <button
                type="button"
                onClick={openNew}
                disabled={!organizationId}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-[13px] font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-50"
              >
                <SafeIcon icon={FiPlus} />
                New Customer
              </button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              title="Total Customers"
              value={customers.length}
              subtitle="All customer records"
              icon={FiUsers}
            />

            <SummaryCard
              title="Active Customers"
              value={activeCount}
              subtitle="Available for new business"
              icon={FiCheckCircle}
              color="green"
            />

            <SummaryCard
              title="Outstanding A/R"
              value={
                <MoneyBreakdown
                  amounts={overallSummary}
                  field="outstanding"
                  fallbackCurrency={defaultCurrency}
                />
              }
              subtitle="Unpaid customer invoices"
              icon={FiDollarSign}
              color="amber"
            />

            <SummaryCard
              title="Overdue A/R"
              value={
                <MoneyBreakdown
                  amounts={overallSummary}
                  field="overdue"
                  fallbackCurrency={defaultCurrency}
                />
              }
              subtitle="Past-due balances"
              icon={FiAlertCircle}
              color="rose"
            />
          </div>

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full lg:max-w-sm">
                <SafeIcon
                  icon={FiSearch}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[15px] text-slate-400"
                />
                <input
                  className={`${inputClass} pl-9`}
                  placeholder="Search customers..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                  {[
                    ['active', 'Active'],
                    ['all', 'All'],
                    ['inactive', 'Inactive']
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFilter(value)}
                      className={`rounded-md px-3 py-1.5 text-[12px] font-medium transition ${
                        filter === value
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={exportCSV}
                  disabled={filteredCustomers.length === 0}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  <SafeIcon icon={FiDownload} />
                  Export CSV
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-left">
                <thead className="border-b border-slate-200 bg-slate-50/70">
                  <tr className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
                    <th className="px-5 py-3.5">Customer</th>
                    <th className="px-4 py-3.5">Contact</th>
                    <th className="px-4 py-3.5">Invoices</th>
                    <th className="px-4 py-3.5">Outstanding</th>
                    <th className="px-4 py-3.5">Overdue</th>
                    <th className="px-4 py-3.5">Status</th>
                    <th className="px-5 py-3.5 text-right">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredCustomers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center">
                          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-50">
                            <SafeIcon
                              icon={FiUsers}
                              className="text-[22px] text-slate-300"
                            />
                          </div>

                          <p className="text-[13px] font-medium text-slate-700">
                            No customers found
                          </p>

                          <p className="mt-1 text-[12px] text-slate-400">
                            {customers.length === 0
                              ? 'Create your first customer to get started.'
                              : 'Try adjusting your search or filter.'}
                          </p>

                          {customers.length === 0 && (
                            <button
                              type="button"
                              onClick={openNew}
                              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-[12px] font-medium text-white hover:bg-blue-700"
                            >
                              <SafeIcon icon={FiPlus} />
                              Add Customer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredCustomers.map((customer) => {
                      const summaries =
                        customerSummaries.get(customer.id) || [];

                      const invoiceCount = summaries.reduce(
                        (total, item) => total + item.invoiceCount,
                        0
                      );

                      return (
                        <tr
                          key={customer.id}
                          className="group border-b border-slate-100 transition last:border-0 hover:bg-slate-50/70"
                        >
                          <td className="px-5 py-4">
                            <button
                              type="button"
                              onClick={() => openDetails(customer)}
                              className="flex items-center gap-3 text-left"
                            >
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[12px] font-semibold text-blue-600">
                                {getInitials(customer.name)}
                              </span>

                              <span className="min-w-0">
                                <span className="block max-w-[230px] truncate text-[13px] font-semibold text-slate-800 group-hover:text-blue-600">
                                  {customer.name}
                                </span>

                                <span className="mt-0.5 block text-[11px] text-slate-400">
                                  {customer.customer_number ||
                                    customer.company_name ||
                                    'Customer'}
                                </span>
                              </span>
                            </button>
                          </td>

                          <td className="px-4 py-4">
                            <div className="space-y-1">
                              <p className="max-w-[210px] truncate text-[12px] text-slate-700">
                                {customer.contact_name || '—'}
                              </p>

                              <p className="max-w-[210px] truncate text-[11px] text-slate-400">
                                {customer.email || 'No email'}
                              </p>
                            </div>
                          </td>

                          <td className="px-4 py-4 text-[12px] font-medium text-slate-700">
                            {invoiceCount}
                          </td>

                          <td className="px-4 py-4 text-[12px] font-medium text-slate-800">
                            <MoneyBreakdown
                              amounts={summaries}
                              field="outstanding"
                              fallbackCurrency={customer.currency || defaultCurrency}
                            />
                          </td>

                          <td className="px-4 py-4 text-[12px] font-medium text-slate-700">
                            <MoneyBreakdown
                              amounts={summaries}
                              field="overdue"
                              fallbackCurrency={customer.currency || defaultCurrency}
                            />
                          </td>

                          <td className="px-4 py-4">
                            <StatusBadge active={customer.is_active} />
                          </td>

                          <td className="px-5 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => openDetails(customer)}
                              className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-[12px] font-medium text-blue-600 hover:bg-blue-50"
                            >
                              View
                              <SafeIcon icon={FiChevronRight} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/50 px-5 py-3 text-[11px] text-slate-500">
              <span>
                Showing {filteredCustomers.length} of {customers.length} customers
              </span>

              <span>
                Financial amounts are grouped by currency.
              </span>
            </div>
          </section>

          <div className="mt-4 flex items-start gap-2 text-[11px] text-slate-400">
            <SafeIcon icon={FiInfo} className="mt-0.5 shrink-0" />
            <p>
              Customer balances are calculated from invoice records.
              Draft, voided, and uncollectible invoices are excluded
              from receivables. Deactivating a customer preserves
              existing invoices and accounting history.
            </p>
          </div>
        </>
      )}
    </div>
  );
}